import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Step,
  StepLabel,
  Stepper,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Folder as FolderIcon,
  Work as WorkIcon,
  VpnKey as VpnKeyIcon,
  Autorenew as AutorenewIcon,
  SmartToy as SmartToyIcon,
  Terminal as TerminalIcon,
  HelpOutline as HelpOutlineIcon,
  Explore as ExploreIcon,
  PlayArrow as PlayArrowIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProxyConnection } from '../context/ProxyConnectionContext';
import { isSuperAdminUser } from '../utils/superAdmin';
import {
  markFeatureTourComplete,
  shouldShowFeatureTour,
} from '../utils/onboardingStorage';
import SwaggerIcon from './icons/SwaggerIcon';
import EnvironmentIcon from './icons/EnvironmentIcon';

type FeatureStep = {
  shortLabel: string;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  bullets: string[];
  tip?: string;
  path?: string;
  pathLabel?: string;
};

const FEATURE_STEPS: FeatureStep[] = [
  {
    shortLabel: 'Overview',
    title: 'Your command center',
    subtitle: 'The Dashboard shows project health, recent activity, and quick counts so you always know where you stand.',
    icon: <DashboardIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
    bullets: [
      'See collections, requests, data sets, and test plans at a glance.',
      'Track team activity and execution trends.',
      'Jump into the area you need from the sidebar on the left.',
    ],
    path: '/dashboard',
    pathLabel: 'Open Dashboard',
  },
  {
    shortLabel: 'Projects',
    title: 'Organize your work',
    subtitle: 'Projects group everything for one product or team. Create a project before adding collections and environments.',
    icon: <WorkIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
    bullets: [
      'Create a project for each API or product you test.',
      'Switch projects from the header dropdown.',
      'Invite teammates from Manage Account (admins).',
    ],
    path: '/projects',
    pathLabel: 'Open Projects',
  },
  {
    shortLabel: 'Environments',
    title: 'Environments & variables',
    subtitle: 'Point requests at dev, staging, or production using environment base URLs and reusable variables.',
    icon: <EnvironmentIcon fontSize="large" style={{ width: 48, height: 48, color: '#1976d2' }} />,
    bullets: [
      'Define environments with base URLs (e.g. https://api.staging.example.com).',
      'Use .Env for secrets and shared keys like {{apiKey}}.',
      'Variables can be project, collection, request, or environment scoped.',
    ],
    path: '/environments',
    pathLabel: 'Open Environments',
  },
  {
    shortLabel: 'Collections',
    title: 'Build & run API requests',
    subtitle: 'Collections are the core of CRUD Expert—like Postman folders with requests, data sets, assertions, and rules.',
    icon: <FolderIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
    bullets: [
      'Create requests (GET, POST, etc.) with headers, body, and auth.',
      'Add data sets for parameterized runs and assertions for validation.',
      'Use Rule Builder for conditional flows (if/then, delays, run request).',
      'Click Send to execute through the CRUD Expert desktop agent.',
    ],
    tip: 'Start here after setup—import cURL or build your first GET request.',
    path: '/collections',
    pathLabel: 'Open Collections',
  },
  {
    shortLabel: 'Test Plans',
    title: 'Automate test cycles',
    subtitle: 'Test Plans run many requests in order—great for smoke tests, regression, and CI-style checks.',
    icon: <AutorenewIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
    bullets: [
      'Pick collections and requests to include in a plan.',
      'Run request-by-request or round-robin by data set.',
      'View pass/fail results, history, and email reports.',
    ],
    path: '/test-plans',
    pathLabel: 'Open Test Plans',
  },
  {
    shortLabel: 'More tools',
    title: 'Swagger, AI Agent & Console',
    subtitle: 'Speed up authoring and debug runs with imports, AI help, and live proxy logs.',
    icon: <SwaggerIcon style={{ width: 48, height: 48 }} />,
    bullets: [
      'Swagger — import OpenAPI specs into collections.',
      'AI Agent — describe tests in plain language and generate requests.',
      'Console — inspect proxy traffic (Ctrl+Alt+C / ⌘+Option+C).',
      '.Env & Functions — reusable values and helper logic.',
    ],
    path: '/swagger',
    pathLabel: 'Try Swagger import',
  },
  {
    shortLabel: 'Ready',
    title: "You're ready to go",
    subtitle: 'A typical first session: create a project → add an environment → build a collection → send a request.',
    icon: <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />,
    bullets: [
      'Help in the sidebar has guides and search.',
      'Proxy Settings if you change machines or reinstall the agent.',
      'Need assistance? Use Help chat or contact your admin.',
    ],
    path: '/collections',
    pathLabel: 'Start with Collections',
  },
];

/** Above app header / sidebar so the tour title stays fully visible. */
const FEATURE_TOUR_DIALOG_Z_INDEX = (theme: { zIndex: { modal: number } }) => theme.zIndex.modal + 10;
const PROXY_SETUP_COMPLETE_EVENT = 'crud:proxy-setup-complete';

const NewUserFeatureTour: React.FC = () => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const { isConnected, initialCheckComplete } = useProxyConnection();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const isSuperAdmin = isSuperAdminUser(user);
  // Admin-only accounts only access Manage Account — skip "How to use" tour.
  const isAdminOnly = user?.role === 'ADMIN';

  const syncTourOpen = useCallback(() => {
    if (authLoading || !isAuthenticated || isSuperAdmin || isAdminOnly || !user?.id) {
      setOpen(false);
      return;
    }
    // Do not show "How Tool Works" while proxy is missing — Proxy Setup Overview takes priority.
    if (!initialCheckComplete || !isConnected) {
      setOpen(false);
      return;
    }
    setOpen(shouldShowFeatureTour(user.id));
  }, [authLoading, isAuthenticated, isSuperAdmin, isAdminOnly, user?.id, initialCheckComplete, isConnected]);

  useEffect(() => {
    syncTourOpen();
  }, [syncTourOpen]);

  useEffect(() => {
    const handleProxySetupComplete = () => {
      syncTourOpen();
    };
    window.addEventListener(PROXY_SETUP_COMPLETE_EVENT, handleProxySetupComplete);
    return () => window.removeEventListener(PROXY_SETUP_COMPLETE_EVENT, handleProxySetupComplete);
  }, [syncTourOpen]);

  const finishTour = useCallback(() => {
    if (!user?.id) return;
    markFeatureTourComplete(user.id);
    setOpen(false);
  }, [user?.id]);

  const goToFeature = useCallback(
    (path: string) => {
      if (!user?.id) return;
      markFeatureTourComplete(user.id);
      setOpen(false);
      navigate(path);
    },
    [navigate, user?.id]
  );

  const step = FEATURE_STEPS[activeStep];
  const isLastStep = activeStep >= FEATURE_STEPS.length - 1;

  const stepContent = useMemo(() => {
    if (!step) return null;
    return (
      <Box>
        <Box sx={{ textAlign: 'center', mb: 2 }}>{step.icon}</Box>
        <Typography variant="h5" fontWeight={600} gutterBottom textAlign="center">
          {step.title}
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph textAlign="center">
          {step.subtitle}
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
          <List dense disablePadding>
            {step.bullets.map((bullet) => (
              <ListItem key={bullet} disableGutters sx={{ alignItems: 'flex-start', py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32, mt: 0.25 }}>
                  <PlayArrowIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText primary={bullet} primaryTypographyProps={{ variant: 'body2' }} />
              </ListItem>
            ))}
          </List>
        </Paper>
        {step.tip && (
          <Chip
            icon={<ExploreIcon />}
            label={step.tip}
            color="primary"
            variant="outlined"
            sx={{ mt: 2 }}
          />
        )}
        {step.path && step.pathLabel && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button variant="outlined" onClick={() => goToFeature(step.path!)}>
              {step.pathLabel}
            </Button>
          </Box>
        )}
      </Box>
    );
  }, [step, goToFeature]);

  if (!open) return null;

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return;
      }}
      aria-labelledby="new-user-feature-tour-title"
      sx={{
        zIndex: FEATURE_TOUR_DIALOG_Z_INDEX,
        '& .MuiDialog-container': {
          // flex-start keeps the welcome title on-screen (center can clip the top).
          alignItems: 'flex-start',
          justifyContent: 'center',
          overflowY: 'auto',
          pt: 3,
          pb: 2,
          px: 2,
        },
        '& .MuiDialog-paper': {
          margin: '0 auto',
        },
      }}
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          width: 'min(720px, calc(100vw - 48px))',
          maxWidth: 'min(720px, calc(100vw - 48px))',
          maxHeight: 'calc(100vh - 80px)',
          m: 0,
          boxSizing: 'border-box',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle id="new-user-feature-tour-title" sx={{ px: 3, pt: 2, pb: 1, flexShrink: 0 }}>
        <Typography component="span" variant="h6">
          Welcome — here&apos;s how CRUD Expert works
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          A quick tour of the main features. Use Next to walk through, or skip when you&apos;re ready
          to explore on your own.
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ overflowY: 'auto', overflowX: 'hidden', px: 3, py: 1, flex: '1 1 auto' }}>
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{
            mb: 2,
            width: '100%',
            overflowX: 'auto',
            '& .MuiStepLabel-label': {
              fontSize: '0.7rem',
              lineHeight: 1.2,
            },
          }}
        >
          {FEATURE_STEPS.map((s) => (
            <Step key={s.shortLabel}>
              <StepLabel>{s.shortLabel}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {stepContent}
        <Box
          sx={{
            mt: 3,
            pt: 2,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            justifyContent: 'center',
          }}
        >
          <Chip size="small" icon={<FolderIcon />} label="Collections" variant="outlined" />
          <Chip size="small" icon={<AutorenewIcon />} label="Test Plans" variant="outlined" />
          <Chip size="small" icon={<EnvironmentIcon />} label="Environments" variant="outlined" />
          <Chip size="small" icon={<VpnKeyIcon />} label=".Env" variant="outlined" />
          <Chip size="small" icon={<SmartToyIcon />} label="AI Agent" variant="outlined" />
          <Chip size="small" icon={<TerminalIcon />} label="Console" variant="outlined" />
          <Chip size="small" icon={<HelpOutlineIcon />} label="Help" variant="outlined" />
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 1.5,
          flexShrink: 0,
          justifyContent: 'space-between',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Button color="inherit" onClick={finishTour}>
          Skip tour
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button disabled={activeStep === 0} onClick={() => setActiveStep((s) => s - 1)}>
            Back
          </Button>
          {!isLastStep ? (
            <Button variant="contained" onClick={() => setActiveStep((s) => s + 1)}>
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={() => goToFeature(step?.path || '/collections')}
            >
              Get started
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default NewUserFeatureTour;
